/**
 * Client Logger — Buffered Browser Telemetry
 *
 * Browser counterpart to logger.ts (Pino + OTel). Same API shape,
 * completely different transport: warn/error are buffered in memory
 * and flushed to the Hono BFF via navigator.sendBeacon().
 *
 * Architecture:
 *   Browser → buffer warn+error → sendBeacon('/api/telemetry/logs')
 *   → Hono BFF → Pino stdout → CloudWatch → Grafana Loki
 *
 * info/debug are local-only (dev console) — never transmitted.
 *
 * Deduplication: identical level+msg within 2 seconds is dropped,
 * preventing React error-loop cascades from flooding the buffer.
 *
 * Lifecycle: flushes on visibilitychange→hidden and pagehide,
 * guaranteeing delivery even when the user closes the tab.
 *
 * Trace correlation: reads window.__TRACE_ID__ (injected during SSR)
 * to correlate browser logs with the originating server request.
 *
 * @example
 *   import { clientLogger } from '@/shared/lib/client-logger';
 *
 *   clientLogger.error({ error, component: 'GoogleSignIn' }, 'OAuth popup failed');
 *   clientLogger.warn({ component: 'AddressMap' }, 'Google Maps API key missing');
 */

import { create, toJsonString, type JsonObject } from '@bufbuild/protobuf';
import { TELEMETRY_MAX_BATCH_EVENTS } from '@letsgethome/lib/telemetry';
import { BrowserLogBatchSchema } from '@letsgethome/proto/telemetry/v1/telemetry_pb';

type LogLevel = 'error' | 'warn' | 'info' | 'debug';

// In-memory buffer item. Mirrors the proto `telemetry.v1.BrowserLogEvent`
// field set; the buffer is converted to that message (via `create` +
// `toJsonString`) at flush, so the wire is proto3-canonical JSON.
interface LogEvent {
  level: LogLevel;
  msg: string;
  context: Record<string, unknown>;
  ts: number;
  url: string;
  traceId?: string;
}

const FLUSH_INTERVAL_MS = 5_000;
// Flush threshold = the ingest endpoint's per-request cap, ONE shared
// number (Rust `domain_core::telemetry` SSOT). A local literal here could
// silently outgrow the server cap, and the overflow would be dropped.
const MAX_BUFFER_SIZE = TELEMETRY_MAX_BATCH_EVENTS;
const DEDUP_WINDOW_MS = 2_000;
const TELEMETRY_ENDPOINT = '/api/telemetry/logs';
const TRANSMIT_LEVELS: ReadonlySet<LogLevel> = new Set(['error', 'warn']);

const SENSITIVE_PATTERNS = [
  'password',
  'token',
  'secret',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'phonenumber',
  'phone',
  'email',
  'cookie',
  'authorization',
];

class ClientLogger {
  private buffer: LogEvent[] = [];
  private recentMessages = new Map<string, number>();

  constructor() {
    if (typeof window === 'undefined') return;

    // Page-lifetime singleton: the interval lives until unload, so the handle
    // is intentionally not retained (no teardown path to clear it).
    setInterval(() => this.flush(), FLUSH_INTERVAL_MS);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush();
    });
    window.addEventListener('pagehide', () => this.flush());
  }

  error(context: Record<string, unknown>, msg: string): void {
    this.log('error', context, msg);
  }

  warn(context: Record<string, unknown>, msg: string): void {
    this.log('warn', context, msg);
  }

  info(context: Record<string, unknown>, msg: string): void {
    this.log('info', context, msg);
  }

  debug(context: Record<string, unknown>, msg: string): void {
    this.log('debug', context, msg);
  }

  flush(): void {
    if (this.buffer.length === 0) return;

    const batch = create(BrowserLogBatchSchema, {
      events: this.buffer.map((e) => ({
        level: e.level,
        msg: e.msg,
        // Sanitized upstream to JSON-safe values; the Struct field's init
        // type is `JsonObject` (same coercion the old JSON.stringify applied).
        context: e.context as JsonObject,
        ts: BigInt(e.ts),
        url: e.url,
        ...(e.traceId !== undefined ? { traceId: e.traceId } : {}),
      })),
    });
    this.buffer = [];

    const payload = toJsonString(BrowserLogBatchSchema, batch);
    const blob = new Blob([payload], { type: 'application/json' });
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(TELEMETRY_ENDPOINT, blob);
    } else {
      fetch(TELEMETRY_ENDPOINT, {
        method: 'POST',
        body: blob,
        keepalive: true,
      }).catch(() => {
        /* best-effort */
      });
    }
  }

  // -- internals --

  private log(level: LogLevel, context: Record<string, unknown>, msg: string): void {
    if (typeof window === 'undefined') return;

    const consoleFn = level === 'debug' ? console.log : console[level];
    consoleFn.call(console, `[${level.toUpperCase()}]`, msg, context);

    if (!TRANSMIT_LEVELS.has(level)) return;

    const now = Date.now();
    const dedupKey = `${level}:${msg}`;
    const lastTime = this.recentMessages.get(dedupKey);
    if (lastTime && now - lastTime < DEDUP_WINDOW_MS) return;
    this.recentMessages.set(dedupKey, now);

    if (this.recentMessages.size > 100) {
      for (const [key, time] of this.recentMessages) {
        if (now - time > DEDUP_WINDOW_MS) this.recentMessages.delete(key);
      }
    }

    this.buffer.push({
      level,
      msg,
      context: this.sanitize(context),
      ts: now,
      url: window.location.href,
      // SSR-injected trace correlation id. Declared on Window in
      // `entry-client.tsx`; cast locally so this module does not
      // depend on the entry-side ambient augmentation.
      traceId: (window as Window & { __TRACE_ID__?: string }).__TRACE_ID__,
    });

    if (this.buffer.length >= MAX_BUFFER_SIZE) this.flush();
  }

  private sanitize(context: Record<string, unknown>): Record<string, unknown> {
    const clean: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(context)) {
      if (SENSITIVE_PATTERNS.some((p) => key.toLowerCase().includes(p))) {
        clean[key] = '[REDACTED]';
      } else if (value instanceof Error) {
        clean[key] = { name: value.name, message: value.message, stack: value.stack };
      } else {
        clean[key] = value;
      }
    }
    return clean;
  }
}

export const clientLogger = new ClientLogger();
